// src/pages/supplyList/SupplyListTable.jsx
import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Typography, Box
} from '@mui/material';

/**
 * 🚀 供給履歴テーブル表示コンポーネント
 * テーブル描画ロジックを独立させて再利用性向上
 */
const SupplyListTable = ({ 
  supplies, 
  onRowClick, 
  loading = false 
}) => {

  // データが空の場合の表示
  if (!Array.isArray(supplies) || supplies.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          {loading ? 'データを読み込み中...' : '供給履歴がありません'}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ 
              width: '16%', 
              whiteSpace: 'nowrap',
              fontWeight: 'bold'
            }}>
              日付
            </TableCell>
            <TableCell sx={{ 
              width: '30%', 
              whiteSpace: 'nowrap',
              fontWeight: 'bold'
            }}>
              顧客名
            </TableCell>
            <TableCell sx={{ 
              width: '20%', 
              whiteSpace: 'nowrap',
              fontWeight: 'bold'
            }}>
              製品名
            </TableCell>
            <TableCell sx={{ 
              width: '17%', 
              textAlign: 'right',
              whiteSpace: 'nowrap',
              minWidth: '80px',
              fontWeight: 'bold'
            }}>
              供給量 (L)
            </TableCell>
            <TableCell sx={{ 
              width: '17%', 
              whiteSpace: 'nowrap',
              fontWeight: 'bold'
            }}>
              種別
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {supplies.map((supply) => (
            <SupplyTableRow 
              key={supply.id}
              supply={supply}
              onClick={() => onRowClick(supply)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/**
 * 🔹 個別の供給履歴行コンポーネント
 * 行の描画ロジックを分離してメンテナンス性向上
 */
const SupplyTableRow = ({ supply, onClick }) => {
  
  // 日付フォーマット（安全な変換）
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '無効な日付';
      return date.toLocaleDateString('ja-JP');
    } catch {
      return '無効な日付';
    }
  };

  // 供給量フォーマット
  const formatAmount = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) return '0';
    return amount.toLocaleString();
  };

  // 🔧 修正：特例判定の安全な取得
  const isExceptional = supply.exceptional || supply.isExceptional || false;

  return (
    <TableRow 
      hover 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      {/* 日付列 */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {formatDate(supply.supplyDate)}
      </TableCell>
      
      {/* 顧客名列 */}
      <TableCell sx={{ 
        maxWidth: '150px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
      title={supply.clientName} // ツールチップ表示
      >
        {supply.clientName || '不明'}
      </TableCell>
      
      {/* 製品名列 */}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {supply.productName || "アドブルー"}
      </TableCell>
      
      {/* 供給量列 */}
      <TableCell sx={{ 
        textAlign: 'right',
        whiteSpace: 'nowrap',
        paddingRight: 2,
        fontWeight: 'medium'
      }}>
        {formatAmount(supply.amount)} L
      </TableCell>
      
      {/* 種別列 */}
      <TableCell>
        {/* 🔧 修正：APIレスポンスのプロパティ名に対応 */}
        <Chip
          label={isExceptional ? '特例' : '通常'}
          color={isExceptional ? 'warning' : 'success'}
          size="small"
          sx={{ 
            fontSize: '0.75rem',
            minWidth: '50px'
          }}
        />
      </TableCell>
    </TableRow>
  );
};

export default SupplyListTable;